import { CheckboxField } from '@mgmresorts/wcl-core';
import { Typography } from '@mgmresorts/wcl-lab';
import React, { useCallback, useContext } from 'react';

import {
  POLICY_URL,
  RULES_URL,
  TERMS_OF_USE_URL,
  CALIFORNIA_NOTICE_URL,
  CALIFORNIA_FINANCE_INCENTIVE_URL,
  COLORADO_PROGRAM_NOTICE_URL,
} from '../../../../config/config.app';
import { BindCheckbox } from '../../../../hooks/useCheckbox';
import { useContent } from '../../../../hooks/useContent';
import { SignUpFormContext } from '../../../../pages/SignUp/SignUpFormContext';
import { Link } from '../../Link';

import { StyledContent } from './StyledContent';

type PolicyCheckboxProps = BindCheckbox & {
  disabled?: boolean;
  onRulesClick?: () => void;
  onPolicyClick?: () => void;
  onTermsOfUseClick?: () => void;
  onCaliforniaNoticeClick?: () => void;
  onCaliforniaFinanceIncentiveClick?: () => void;
  onColoradoProgramNoticeClick?: () => void;
};

export function PolicyCheckbox({
  onRulesClick,
  onPolicyClick,
  onTermsOfUseClick,
  onCaliforniaNoticeClick,
  onCaliforniaFinanceIncentiveClick,
  onColoradoProgramNoticeClick,
  ...props
}: PolicyCheckboxProps) {
  const { isCanadian } = useContext(SignUpFormContext);
  const { getContentByPath } = useContent();

  const handleRulesClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onRulesClick && onRulesClick();
      window.open(RULES_URL);
    },
    [onRulesClick],
  );

  const handlePolicyClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onPolicyClick && onPolicyClick();
      window.open(POLICY_URL);
    },
    [onPolicyClick],
  );

  const handleTermsOfUseClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onTermsOfUseClick && onTermsOfUseClick();
      window.open(TERMS_OF_USE_URL);
    },
    [onTermsOfUseClick],
  );

  const handleCaliforniaNoticeClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onCaliforniaNoticeClick && onCaliforniaNoticeClick();
      window.open(CALIFORNIA_NOTICE_URL);
    },
    [onCaliforniaNoticeClick],
  );

  const handleCaliforniaFinanceNoticeClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onCaliforniaFinanceIncentiveClick && onCaliforniaFinanceIncentiveClick();
      window.open(CALIFORNIA_FINANCE_INCENTIVE_URL);
    },
    [onCaliforniaFinanceIncentiveClick],
  );

  const handleColoradoProgramNoticeClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      onColoradoProgramNoticeClick && onColoradoProgramNoticeClick();
      window.open(COLORADO_PROGRAM_NOTICE_URL);
    },
    [onColoradoProgramNoticeClick],
  );

  const renderCanadianPolicyContent = useCallback(() => {
    const [preRulesLink, afterRulesLink] = getContentByPath<string>('shared.fields.policy.canadianTitle').split(
      '%programRules%',
    );
    const [preTermsOfUse, postTermsOfUse] = afterRulesLink.split('%termsOfUse%');
    const [prePolicy, afterPolicy] = postTermsOfUse.split('%privacyPolicy%');
    const rulesLink = getContentByPath<string>('shared.fields.policy.programRules');
    const policyLink = getContentByPath<string>('shared.fields.policy.privacyPolicy');
    const termsOfUseLink = getContentByPath<string>('shared.fields.policy.termsOfUse');

    return (
      <Typography variant='paragraph' testId='policy-checkbox-content'>
        {preRulesLink}
        <Link href={RULES_URL} onClick={handleRulesClick} data-testid='policy-checkbox-rules'>
          {rulesLink}
        </Link>
        {preTermsOfUse}
        <Link href={TERMS_OF_USE_URL} onClick={handleTermsOfUseClick} data-testid='policy-checkbox-terms-of-use'>
          {termsOfUseLink}
        </Link>
        {prePolicy}
        <Link href={POLICY_URL} onClick={handlePolicyClick} data-testid='policy-checkbox-policy'>
          {policyLink}
        </Link>
        {afterPolicy}
      </Typography>
    );
  }, [getContentByPath, handlePolicyClick, handleRulesClick, handleTermsOfUseClick]);

  const renderPolicyContent = useCallback(() => {
    const [preRulesLink, afterRulesLink] =
      getContentByPath<string>('shared.fields.policy.title').split('%programRules%');
    const [preTermsOfUse, postTermsOfUse] = afterRulesLink.split('%termsOfUse%');
    const [prePolicy, afterPolicy] = postTermsOfUse.split('%privacyPolicy%');
    const [preCaliforniaNotice, postCaliforniaNotice] = afterPolicy.split('%californiaNotice%');
    const [preCaliforniaFinancialIncentive, postCaliforniaFinancialIncentive] = postCaliforniaNotice.split(
      '%californiaFinancialIncentive%',
    );
    const [preColoradoProgramNotice] = postCaliforniaFinancialIncentive.split('%coloradoProgramNotice%');
    const rulesLink = getContentByPath<string>('shared.fields.policy.programRules');
    const policyLink = getContentByPath<string>('shared.fields.policy.privacyPolicy');
    const termsOfUseLink = getContentByPath<string>('shared.fields.policy.termsOfUse');
    const californiaNoticeLink = getContentByPath<string>('shared.fields.policy.californiaNotice');
    const californiaFinancialIncentiveLink = getContentByPath<string>(
      'shared.fields.policy.californiaFinancialIncentive',
    );
    const coloradoProgramNoticeLink = getContentByPath<string>('shared.fields.policy.coloradoProgramNotice');

    return (
      <Typography variant='paragraph' testId='policy-checkbox-content'>
        {preRulesLink}
        <Link href={RULES_URL} onClick={handleRulesClick} data-testid='policy-checkbox-rules'>
          {rulesLink}
        </Link>
        {preTermsOfUse}
        <Link href={TERMS_OF_USE_URL} onClick={handleTermsOfUseClick} data-testid='policy-checkbox-terms-of-use'>
          {termsOfUseLink}
        </Link>
        {prePolicy}
        <Link href={POLICY_URL} onClick={handlePolicyClick} data-testid='policy-checkbox-policy'>
          {policyLink}
        </Link>
        {preCaliforniaNotice}
        <Link
          href={CALIFORNIA_NOTICE_URL}
          onClick={handleCaliforniaNoticeClick}
          data-testid='policy-checkbox-california-notice'
        >
          {californiaNoticeLink}
        </Link>
        {preCaliforniaFinancialIncentive}
        <Link
          href={CALIFORNIA_FINANCE_INCENTIVE_URL}
          onClick={handleCaliforniaFinanceNoticeClick}
          data-testid='policy-checkbox-california-finance-notice'
        >
          {californiaFinancialIncentiveLink}
        </Link>
        {preColoradoProgramNotice}
        <Link
          href={COLORADO_PROGRAM_NOTICE_URL}
          onClick={handleColoradoProgramNoticeClick}
          data-testid='policy-checkbox-colorado-program-notice'
        >
          {coloradoProgramNoticeLink}
        </Link>
      </Typography>
    );
  }, [
    getContentByPath,
    handlePolicyClick,
    handleRulesClick,
    handleTermsOfUseClick,
    handleCaliforniaNoticeClick,
    handleCaliforniaFinanceNoticeClick,
    handleColoradoProgramNoticeClick,
  ]);

  return (
    <StyledContent data-testid='policy-checkbox'>
      <CheckboxField
        label={isCanadian ? renderCanadianPolicyContent() : renderPolicyContent()}
        labelPlacement='right'
        id='policy'
        {...props}
      />
    </StyledContent>
  );
}

PolicyCheckbox.displayName = 'PolicyCheckboxField';
